import React, { FunctionComponent, useState, useEffect } from 'react';
import { Icon, Button } from 'semantic-ui-react';
import classnames from 'classnames';
import withStyles from 'react-jss';
import { useFormContext } from 'react-hook-form';
import { pathOr, propOr } from 'ramda';

import ImagePlaceholder from 'assets/icons/image-placeholder.png';
import { ClassProp } from 'helpers/types';
import { removeSpecialCharsAndUpperCase } from 'helpers/stringFunctions';
import { Theme } from 'config';

import FormErrors from '../FormError';
import {
  AssetBundle,
  AssetName,
  CLOUDINARY_WIDGET_OPTIONS,
  loadCloudinary,
} from './UploadWidget.helpers';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  name: string;
  label: string;
  merchantName: string;
  assetBundle: AssetBundle;
  assetName: AssetName;
  register: any;
  defaultImage?: string;
  size?: string;
  tabIndex?: number;
  assetFolder?: string;
  filePrefix?: string;
  assetFileName?: string;
};

const styles = (theme: Theme) => ({
  container: {
    width: '100%',
    marginTop: '.25rem !important',
    justifyContent: 'center !important',
  },
  small: {
    width: '40%',
    marginRight: '.50rem',
  },
  medium: {
    width: '50%',
    marginRight: '.50rem',
  },
  large: {
    width: '100%',
  },
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  fieldLabel: {
    color: '#646464 !important',
    fontSize: '.875rem !important',
    fontWeight: '500 !important',
    margin: '0 0 .30rem 0',
    display: 'block !important',
  },
  fieldInput: {
    display: 'flex',
    flex: '1 0 auto',
    maxWidth: '100%',
    width: '100%',
    textAlign: 'left',
    margin: '0',
    outline: '0',
    tapHighlightColor: 'rgba(255,255,255,0)',
    lineHeight: '1.21em',
    padding: '.67857143rem 1rem',
    fontSize: '1em',
    background: '#fff',
    border: '.0625rem solid rgba(34,36,38,.15)',
    color: 'rgba(0,0,0,.87)',
    borderRadius: '.28571429rem',
    boxShadow: '0 0 0 0 transparent inset',
    transition: 'color .1s ease,border-color .1s ease',
  },
  fieldError: {},
  imageContainer: {
    width: '100%',
    marginTop: '.25rem !important',
    justifyContent: 'center !important',
    display: 'inline-block',
  },
  logo: {
    width: '8rem',
    height: '8rem',
  },
  card: {
    width: '25rem',
    height: '15rem',
  },
  banner: {
    width: '40rem',
    height: '15rem',
  },
  tile: {
    width: '20rem',
    height: '20rem',
  },
  uploadButton: {
    backgroundColor: `${theme.violet} !important`,
    borderRadius: '1.5em !important',
    color: '#fff !important',
    marginTop: '1rem !important',
    fontWeight: 'bold !important',
    width: '10rem !important',
    outline: 0,
    border: 'none !important',
    '&:hover': {
      boxShadow:
        '0 0 0 .0625rem transparent inset,0 0 0 0 rgba(34,36,38,.15) inset !important',
      opacity: 0.9,
    },
    '&:active': {
      opacity: 0.7,
    },
  },
});

const onUploadClick = (
  brandId: string,
  assetBundle: AssetBundle,
  assetName: AssetName,
  setImage: Function,
  setImgInfo: Function,
  handleSetError: Function,
  handleClearErrors: Function,
  assetFolder: string,
  filePrefix: string,
  assetFileName: string,
) => () => {
  if (!brandId) {
    handleSetError('Merchant name can not be blank!');
  } else {
    handleClearErrors();
  }

  if (brandId && assetName) {
    const brandFolder = removeSpecialCharsAndUpperCase(brandId);
    const folder =
      assetFolder || `brand assets/${brandFolder}/${assetBundle}/${assetName}`;

    const fileName = assetFileName || `${filePrefix}${Date.now()}`;

    // @ts-expect-error Cloudinary
    window.cloudinary.openUploadWidget(
      {
        ...CLOUDINARY_WIDGET_OPTIONS,
        folder,
        publicId: fileName,
      },
      (err: any, info: any) => {
        if (!err && info.event === 'success') {
          setImage(pathOr('', ['info', 'secure_url'], info));
          setImgInfo(JSON.stringify(propOr({}, 'info', info)));
        }
      },
    );
  }
};

const UploadWidget: FunctionComponent<Props> = ({
  classes,
  label,
  name,
  assetName,
  assetBundle,
  assetFolder = '',
  filePrefix = '',
  assetFileName = '',
  merchantName,
  defaultImage,
  size,
  register,
}) => {
  const [imageUrl, setImageUrl] = useState<string>('');
  const [imageInfo, setImgInfo] = useState<string>('');
  const { errors, setError, clearErrors } = useFormContext();

  useEffect(() => {
    loadCloudinary();
  }, []);

  const handleSetError = (message: string) => setError(name, { message });

  const handleClearErrors = () => clearErrors([name]);

  const handleUpload = onUploadClick(
    merchantName,
    assetBundle,
    assetName,
    setImageUrl,
    setImgInfo,
    handleSetError,
    handleClearErrors,
    assetFolder,
    filePrefix,
    assetFileName,
  );

  return (
    <div
      className={classnames(classes.container, {
        [classes.small]: size === 'small',
        [classes.medium]: size === 'medium',
        [classes.large]: size === 'large',
      })}
    >
      <label className={classes.fieldContainer} htmlFor={name}>
        <span className={classes.fieldLabel}>{label}</span>
        <img
          src={imageUrl || defaultImage || ImagePlaceholder}
          className={classnames(classes.imageContainer, {
            [classes.logo]: assetName === `${AssetName.LOGO}`,
            [classes.card]: assetName === `${AssetName.CARD}`,
            [classes.banner]: assetName === `${AssetName.BANNER}`,
            [classes.tile]: assetName === `${AssetName.TILE}`,
          })}
        />
        <Button
          className={classes.uploadButton}
          type="button"
          onClick={handleUpload}
        >
          <Icon name="upload" />
          Image
        </Button>

        <input
          type="hidden"
          id={name}
          name={name}
          ref={register}
          value={imageInfo}
        />
      </label>
      {errors && <FormErrors name={name} errors={errors} />}
    </div>
  );
};

export default withStyles(styles)(UploadWidget);
